// Skjutmenyn räknas i API:t (apps/api/src/lib/skjutmeny.ts): officiell jakttid i markens län,
// tid på dygnet för positionen och avskjutningsplanen, med pris för den som tittar. Samma
// rader används i kartans skjutmeny, planeraren och inbjudan till jakt (J1) – här finns bara
// presentationen.
import type { GameStatus, MarkSkjutmeny, SkjutmenyRow } from '../api';

export type { GameStatus };

export const GAME_STATUS_LABEL: Record<GameStatus, string> = {
  lovligt: 'Lovligt',
  fel_tid: 'Fel tid på dygnet',
  slut: 'Slut i planen',
  okand: 'Jakttid okänd',
  ej_jakttid: 'Ej jakttid',
};

/**
 * Kort text under viltslaget. "okand" kan gälla jakttiden eller tiden på dygnet ("Tid på dygnet okänd: position
 * saknas"); förr stod det alltid "Jakttid okänd". Slut i planen visar orsaken.
 */
export function statusText(r: { status: GameStatus; reason?: string | null }): string {
  if (r.status === 'slut' && r.reason) return r.reason;
  if (r.status === 'okand' && r.reason) return r.reason.split(':')[0].trim() || GAME_STATUS_LABEL.okand;
  return GAME_STATUS_LABEL[r.status];
}

const ORDER: Record<GameStatus, number> = { lovligt: 0, fel_tid: 1, slut: 2, okand: 3, ej_jakttid: 4 };
export const statusRank = (s: GameStatus) => ORDER[s] ?? 9;

export interface SkjutmenyFamily<R> { familyKey: string; familyLabel: string; rows: R[] }

/** Raderna per familj; familjer med något lovligt först, annars i API:ts ordning. */
export function groupByFamily<R extends Pick<SkjutmenyRow, 'familyKey' | 'familyLabel' | 'status'>>(rows: R[]): SkjutmenyFamily<R>[] {
  const out: SkjutmenyFamily<R>[] = [];
  for (const r of rows) {
    const g = out.find(x => x.familyKey === r.familyKey);
    if (g) g.rows.push(r); else out.push({ familyKey: r.familyKey, familyLabel: r.familyLabel, rows: [r] });
  }
  const best = (g: SkjutmenyFamily<R>) => Math.min(...g.rows.map(r => statusRank(r.status)));
  return out.map((g, i) => ({ g, i })).sort((a, b) => best(a.g) - best(b.g) || a.i - b.i).map(x => x.g);
}

/** Alla rader: övrigt vilt och fågelsektionen (SM2, tom när fågel är avstängd). */
export const allRows = (m: Pick<MarkSkjutmeny, 'rows' | 'birds'>): SkjutmenyRow[] => [...m.rows, ...(m.birds?.rows ?? [])];

/** En rad i inbjudans skjutmeny. planned/remaining är null när viltslaget inte finns i planen. */
export interface HuntGameRow {
  gameType: string;
  familyLabel: string;
  planned: number | null;
  remaining: number | null;
  status: GameStatus;
  reason?: string | null;
}

/**
 * Inbjudans skjutmeny för jaktdagen (samma urval som API:t sparar): viltslag som varken har
 * jakttid den dagen eller finns i planen tas inte med. Lovliga först.
 */
export function inviteRows(rows: SkjutmenyRow[]): HuntGameRow[] {
  return rows
    .filter(r => !(r.status === 'ej_jakttid' && r.planned == null))
    .map(r => ({ gameType: r.gameType, familyLabel: r.familyLabel, planned: r.planned, remaining: r.remaining, status: r.status, reason: r.reason }))
    .sort((a, b) => statusRank(a.status) - statusRank(b.status));
}

export const kr = (n: number) => `${Math.round(n).toLocaleString('sv-SE')} kr`;
